import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

// Define the API response structure
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Define the User interface matching the legacy JSON structure
export interface User {
  _id: string;
  officeId?: string | null;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  role: string;
  aor: boolean;
  convosoUserId: string;
  profilePicURL: string;
  statusActive: boolean;
  npn: boolean;
  userAccess?: {
    downlineAccess?: boolean;
    // add other access properties if needed
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  officeDownLines?: any;
}

// Define the slice state interface
interface UserState {
  userData: User | null;
  loading: boolean;
  error: string | null;
}

// Set the initial state
const initialState: UserState = {
  userData: null,
  loading: false,
  error: null,
};

// Update with your actual API URL
const apiUrl = process.env.REACT_APP_API_URL;

export const fetchUserData = createAsyncThunk<
  User,
  void,
  { rejectValue: string }
>(
  'userDetails/fetchUserData',
  async (_, thunkAPI) => {
    const token = localStorage.getItem('accessToken');
    //console.log('fetchUserData - Token found:', !!token);
    if (!token) {
      return thunkAPI.rejectWithValue('No access token found');
    }
    try {
      //console.log('fetchUserData - Making request to:', `${apiUrl}/auth/me`);
      const response = await axios.get<ApiResponse<User>>(
        `${apiUrl}/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      //console.log('fetchUserData - Raw response:', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return thunkAPI.rejectWithValue('Invalid response format from server');
      }
    } catch (error: unknown) {
      console.error('fetchUserData - Error:', error); // Debug error
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while fetching user data';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Create the slice with synchronous reducers and extra reducers to handle async thunk states
const userDetails = createSlice({
  name: 'userDetails',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.userData = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearUserData: (state) => {
      state.userData = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error fetching user data';
      });
  },
});

export const { setUser, setLoading, setError, clearUserData } =
  userDetails.actions;
export default userDetails.reducer;
